import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import Navigation from "../../components/Navigation/Navigation";
import back from "../../assets/back.png";
import image from "../../assets/image.png";
import add from "../../assets/add.png";
import { request } from "../../utils/axios-utils";
import { toast } from "react-hot-toast";
import Modal from "../../components/Modal/Modal";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import noImage from "../../assets/no_image.png";

const CategoryPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const pathSnippets = location.pathname.split("/").filter((i) => i);
  const currentSlug = pathSnippets[pathSnippets.length - 1];

  const [activeModal, setActiveModal] = useState("");
  const [loading, setLoading] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    type: "",
    id: "",
  });
  const [error, setError] = useState("");
  const [languageList, setLanguageList] = useState([]);
  const [menuCurrency, setMenuCurrency] = useState("");

  const [category, setCategory] = useState({});
  const [subCategories, setSubCategories] = useState([]);
  const [items, setItems] = useState([]);

  const [categoryData, setCategoryData] = useState({
    selectedNameLang: "",
    name: [],
    image: null,
    available: true,
  });
  const [editCategoryData, setEditCategoryData] = useState({});

  const [itemData, setItemData] = useState({
    name: [],
    description: [],
    image: null,
    weight: "",
    price: 0,
    available: true,
  });
  const [editItemData, setEditItemData] = useState({});

  const addCategory = async () => {
    setError("");

    // Check if the main language is provided
    const mainLanguage = languageList[0]; // Assuming the first language is the main language
    if (!categoryData.name[mainLanguage]) {
      setError(
        `Please enter the name for the main language (${mainLanguage}).`
      );
      return;
    }

    try {
      setLoading("addCategory");
      const formData = new FormData();

      if (categoryData.image) {
        formData.append("image", categoryData.image);
      }
      formData.append("categoryData", JSON.stringify(categoryData));
      formData.append("slug", JSON.stringify(slug));
      formData.append("parentId", JSON.stringify(currentSlug));

      const response = await request({
        url: "/member/add-category",
        method: "POST",
        data: formData,
      });

      setSubCategories((prevData) => [...prevData, response.data.newCategory]);

      toast.success(response.data.message);

      setError("");
    } catch (error) {
      toast.error(error.response?.data?.message || "Error adding category.");
    } finally {
      setLoading("");
      setActiveModal("");

      const languages = languageList.reduce((acc, lang) => {
        acc[lang] = "";
        return acc;
      }, {});
      setCategoryData({
        name: languages,
        description: languages,
        selectedNameLang: languageList[0],
        image: null,
        available: true,
      });
    }
  };

  const editCategory = async () => {
    setError("");

    // Check if the main language is provided
    const mainLanguage = languageList[0]; // Assuming the first language is the main language
    if (!editCategoryData.name[mainLanguage]) {
      setError(
        `Please enter the name for the main language (${mainLanguage}).`
      );
      return;
    }

    try {
      setLoading("editCategory");
      const formData = new FormData();

      if (editCategoryData.newImage) {
        formData.append("image", editCategoryData.newImage);
      }
      formData.append("editCategoryData", JSON.stringify(editCategoryData));
      formData.append("slug", JSON.stringify(slug));

      const response = await request({
        url: "/member/edit-category",
        method: "POST",
        data: formData,
      });

      setSubCategories((prevData) =>
        prevData.map((category) =>
          category.id === response.data.category.id
            ? response.data.category
            : category
        )
      );

      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error editing category.");
    } finally {
      setLoading("");
      setActiveModal("");
      setEditCategoryData({});
    }
  };

  const deleteCategory = async () => {
    try {
      setLoading("deleteCategory");

      const response = await request({
        url: "/member/delete-category",
        method: "POST",
        data: { categoryId: deleteModal.id, slug },
      });

      setSubCategories((prevData) =>
        prevData.filter((category) => category.id !== deleteModal.id)
      );

      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading("");
    }
  };

  const addItem = async () => {
    setError("");

    const mainLanguage = languageList[0]; // Assuming the first language in the languageList is the main language

    // Check if the name is provided for the main language

    if (!itemData.name[mainLanguage]) {
      setError(`Please enter a name for the main language (${mainLanguage}).`);
      return;
    }

    // Check for descriptions:
    // If any description is provided, the main language must also have a value.
    let hasDescription = false;
    for (const lang in itemData.description) {
      if (itemData.description[lang]) {
        hasDescription = true;
        break; // Once we find a description, no need to continue checking
      }
    }

    // If there is any description, check that the main language has one as well
    if (hasDescription && !itemData.description[mainLanguage]) {
      setError(
        `Please provide a description for the main language (${mainLanguage}).`
      );
      return;
    }

    // Check if price is valid
    const price = Number(itemData.price);
    if (isNaN(price) || price <= 0) {
      setError("Please enter a valid price greater than 0.");
      return;
    }

    // Check if any subscription exists and ensure it has a value in the main language
    if (itemData.subscription && !itemData.subscription[mainLanguage]) {
      setError(
        `Please provide a subscription description for the main language (${mainLanguage}).`
      );
      return;
    }

    try {
      setLoading("addItem");

      // Create the form data
      const formData = new FormData();
      formData.append("image", itemData.image);
      formData.append("itemData", JSON.stringify(itemData));
      formData.append("slug", JSON.stringify(slug));
      formData.append("parentId", JSON.stringify(currentSlug));

      const response = await request({
        url: "/member/add-item",
        method: "POST",
        data: formData,
      });

      setItems((prevData) => [...prevData, response.data.newItem]);

      toast.success(response.data.message);

      setError("");
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      // Reset item data
      const languages = languageList.reduce((acc, lang) => {
        acc[lang] = "";
        return acc;
      }, {});
      setItemData({
        name: languages,
        description: languages,
        selectedNameLang: languageList[0],
        selectedDescriptionLang: languageList[0],
        image: null,
        weight: "",
        price: 0,
        available: true,
      });
      setLoading("");
      setActiveModal("");
    }
  };

  const editItem = async () => {
    setError("");

    const mainLanguage = languageList[0]; // Assuming the first language is the main language

    // Check if the name is provided for the main language
    if (!editItemData.name[mainLanguage]) {
      setError(`Please enter a name for the main language (${mainLanguage}).`);
      return;
    }

    // Check if any description exists and if it's provided, ensure the main language has one as well
    let hasDescription = false;
    for (const lang in editItemData.description) {
      if (editItemData.description[lang]) {
        hasDescription = true;
        break; // If any description is found, we break early.
      }
    }

    // If there is any description, the main language must also have one
    if (hasDescription && !editItemData.description[mainLanguage]) {
      setError(
        `Please provide a description for the main language (${mainLanguage}).`
      );
      return;
    }

    // Check if price is valid
    const price = Number(editItemData.price);
    if (isNaN(price) || price <= 0) {
      setError("Please enter a valid price greater than 0.");
      return;
    }

    try {
      setLoading("editItem");

      const formData = new FormData();
      if (editItemData.newImage) {
        formData.append("image", editItemData.newImage); // Adding the new image if exists
      }
      formData.append("editItemData", JSON.stringify(editItemData));
      formData.append("slug", JSON.stringify(slug));

      const response = await request({
        url: "/member/edit-item",
        method: "POST",
        data: formData,
      });

      // Update the item in the list after editing
      setItems((prevData) =>
        prevData.map((item) =>
          item.id === response.data.item.id ? response.data.item : item
        )
      );

      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading("");
      setActiveModal("");
      setEditCategoryData({});
    }
  };

  const deleteItem = async () => {
    try {
      setLoading("deleteItem");

      const response = await request({
        url: "/member/delete-item",
        method: "POST",
        data: { itemId: deleteModal.id, slug },
      });

      setItems((prevData) =>
        prevData.filter((item) => item.id !== deleteModal.id)
      );

      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading("");
    }
  };

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading("data");
        //LanguageList
        const response = await request({
          url: "/member/get-languages",
          method: "POST",
          data: { slug },
        });

        const languages = response.data.languages.reduce((acc, lang) => {
          acc[lang] = "";
          return acc;
        }, {});
        setMenuCurrency(response.data.menuCurrency);

        setCategoryData((prevData) => ({
          ...prevData,
          name: languages,
          description: languages,
          selectedNameLang: response.data.languages[0],
          selectedDescriptionLang: response.data.languages[0],
        }));

        setItemData((prevData) => ({
          ...prevData,
          name: languages,
          description: languages,
          selectedNameLang: response.data.languages[0],
          selectedDescriptionLang: response.data.languages[0],
        }));

        //Category Data
        const categoryResponse = await request({
          url: "/member/get-category-data",
          method: "POST",
          data: { slug, categoryId: currentSlug },
        });

        setLanguageList(response.data.languages);
        setCategory(categoryResponse.data.category);
        setSubCategories(categoryResponse.data.subcategories);
        setItems(categoryResponse.data.items);
      } catch (error) {
        toast.error(error.response.data.message);
      } finally {
        setLoading("");
      }
    };

    getData();
  }, [location.pathname]);

  return (
    <Navigation isLoading={loading === "data"}>
      {activeModal === "addCategory" && (
        <Modal
          heading={"Add Category"}
          paragraph={"Enter information about your category."}
          closeModal={() => {
            setActiveModal("");
            const languages = languageList.reduce((acc, lang) => {
              acc[lang] = "";
              return acc;
            }, {});
            setCategoryData({
              name: languages,
              description: languages,
              selectedNameLang: languageList[0],
              selectedDescriptionLang: languageList[0],
              image: null,
            });
            setError("");
          }}
          btnText={"Add"}
          isLoading={loading === "addCategory"}
          btnFuntion={loading === "addCategory" ? null : addCategory}
        >
          <div className="modal-label-div">
            <label htmlFor="categoryName" className="modal-label">
              Category Name *
            </label>

            <select
              className="language-select"
              onChange={(e) => {
                const selectedLanguage = e.target.value;
                setCategoryData((prevData) => {
                  return { ...prevData, selectedNameLang: selectedLanguage };
                });
              }}
            >
              {languageList.map((language) => {
                return <option key={language}>{language}</option>;
              })}
            </select>
          </div>
          <input
            id="categoryName"
            value={categoryData.name[categoryData.selectedNameLang]}
            onChange={(e) => {
              setCategoryData((prevData) => ({
                ...prevData,
                name: {
                  ...prevData.name,
                  [prevData.selectedNameLang]: e.target.value,
                },
              }));
            }}
            name="name"
            placeholder="Category name"
            className="classic-input mb-16"
            maxLength={20}
          />

          <div className="image-remove-p-div">
            <p
              onClick={() => {
                setCategoryData((prevData) => ({
                  ...prevData,
                  image: null,
                }));
              }}
              className="image-remove-p"
            >
              Remove Image
            </p>
          </div>
          <div className="image-input-div">
            <div
              className={
                categoryData.image ? "image-input border-none" : "image-input"
              }
            >
              {categoryData.image ? (
                <>
                  <img
                    className="image-output"
                    src={
                      categoryData.image.url
                        ? categoryData.image.url
                        : URL.createObjectURL(categoryData.image)
                    }
                  />
                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setCategoryData((prevState) => ({
                          ...prevState,
                          image: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              ) : (
                <>
                  <img className="image-placeholder" src={image} />

                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setCategoryData((prevState) => ({
                          ...prevState,
                          image: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              )}
            </div>
          </div>

          <div className="checkbox-container">
            <input
              checked={categoryData.available}
              onChange={(e) => {
                setCategoryData((prevData) => ({
                  ...prevData,
                  available: e.target.checked,
                }));
              }}
              className="checkbox-input"
              type="checkbox"
              id="customCheckbox"
            />
            <label htmlFor="customCheckbox" className="custom-checkbox"></label>
            <p className="checkbox-p">Available</p>
          </div>

          {error && <p className="login-form-error-p">{error}</p>}
        </Modal>
      )}

      {activeModal === "editCategory" && (
        <Modal
          heading={"Edit Category"}
          paragraph={"Edit information about your category."}
          closeModal={() => {
            setActiveModal("");
            setEditCategoryData({});
            setError("");
          }}
          btnText={"Save"}
          isLoading={loading === "editCategory"}
          btnFuntion={loading === "editCategory" ? null : editCategory}
        >
          <div className="modal-label-div">
            <label htmlFor="categoryName" className="modal-label">
              Category Name *
            </label>

            <select
              className="language-select"
              onChange={(e) => {
                const selectedLanguage = e.target.value;
                setEditCategoryData((prevData) => {
                  return { ...prevData, selectedNameLang: selectedLanguage };
                });
              }}
            >
              {languageList.map((language) => {
                return <option key={language}>{language}</option>;
              })}
            </select>
          </div>
          <input
            id="categoryName"
            value={editCategoryData.name[editCategoryData.selectedNameLang]}
            onChange={(e) => {
              setEditCategoryData((prevData) => ({
                ...prevData,
                name: {
                  ...prevData.name,
                  [prevData.selectedNameLang]: e.target.value,
                },
              }));
            }}
            name="name"
            placeholder="Category name"
            className="classic-input mb-16"
            maxLength={20}
          />

          <div className="image-remove-p-div">
            <p
              onClick={() => {
                setEditCategoryData((prevData) => ({
                  ...prevData,
                  newImage: null,
                  image: null,
                }));
              }}
              className="image-remove-p"
            >
              Remove Image
            </p>
          </div>
          <div className="image-input-div">
            <div
              className={
                !editCategoryData?.image && !editCategoryData.newImage
                  ? "image-input"
                  : "image-input border-none"
              }
            >
              {editCategoryData.image || editCategoryData.newImage ? (
                <>
                  <img
                    className="image-output"
                    src={
                      editCategoryData.newImage
                        ? URL.createObjectURL(editCategoryData.newImage)
                        : editCategoryData.image.url
                    }
                  />
                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setEditCategoryData((prevState) => ({
                          ...prevState,
                          newImage: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              ) : (
                <>
                  <img className="image-placeholder" src={image} />

                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setEditCategoryData((prevState) => ({
                          ...prevState,
                          newImage: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              )}
            </div>
          </div>

          <div className="checkbox-container">
            <input
              checked={editCategoryData.available}
              onChange={(e) => {
                setEditCategoryData((prevData) => ({
                  ...prevData,
                  available: e.target.checked,
                }));
              }}
              className="checkbox-input"
              type="checkbox"
              id="customCheckbox"
            />
            <label htmlFor="customCheckbox" className="custom-checkbox"></label>
            <p className="checkbox-p">Available</p>
          </div>

          {error && <p className="login-form-error-p">{error}</p>}
        </Modal>
      )}

      {deleteModal.type === "deleteCategory" && (
        <DeleteModal
          heading={"Delete category?"}
          paragraph={
            "This will delete all the categories and items that are inside of this category."
          }
          deleteFunction={loading === "deleteCategory" ? null : deleteCategory}
          setDeleteModal={setDeleteModal}
          btnText="Delete"
        ></DeleteModal>
      )}

      {activeModal === "addItem" && (
        <Modal
          heading={"Add Item"}
          paragraph={"Enter information about your item."}
          closeModal={() => {
            setActiveModal("");
            const languages = languageList.reduce((acc, lang) => {
              acc[lang] = "";
              return acc;
            }, {});
            setItemData({
              selectedNameLang: languageList[0],
              selectedDescriptionLang: languageList[0],
              name: languages,
              description: languages,
              image: null,
              weight: "",
              price: 0,
              available: true,
            });
            setError("");
          }}
          btnText={"Add"}
          isLoading={loading === "addItem"}
          btnFuntion={loading === "addItem" ? null : addItem}
        >
          <div className="modal-label-div">
            <label htmlFor="itemName" className="modal-label">
              Item Name *
            </label>

            <select
              className="language-select"
              onChange={(e) => {
                const selectedLanguage = e.target.value;
                setItemData((prevData) => {
                  return { ...prevData, selectedNameLang: selectedLanguage };
                });
              }}
            >
              {languageList.map((language) => {
                return <option key={language}>{language}</option>;
              })}
            </select>
          </div>
          <input
            id="itemName"
            value={itemData.name[itemData.selectedNameLang]}
            onChange={(e) => {
              setItemData((prevData) => ({
                ...prevData,
                name: {
                  ...prevData.name,
                  [prevData.selectedNameLang]: e.target.value,
                },
              }));
            }}
            name="name"
            placeholder="Item name"
            className="classic-input mb-16"
            maxLength={50}
          />

          <div className="modal-double-input-div mb-16">
            <div className="modal-half-input-div">
              <label htmlFor="weight" className="modal-label padding-5-0">
                Weight
              </label>
              <input
                id="weight"
                htmlFor="weight"
                maxLength={10}
                value={itemData.weight}
                onChange={(e) => {
                  setItemData((prevData) => ({
                    ...prevData,
                    weight: e.target.value,
                  }));
                }}
                name="weight"
                placeholder="Weight"
                className="classic-input"
              />
            </div>

            <div className="modal-half-input-div">
              <label htmlFor="price" className="modal-label padding-5-0">
                Price *
              </label>
              <input
                id="price"
                htmlFor="price"
                name="price"
                maxLength={20}
                value={itemData.price}
                onChange={(e) => {
                  const value = e.target.value;
                  // Regex to allow only digits and one dot for decimals, disallowing + and - signs
                  if (/^\d*\.?\d{0,2}$/.test(value)) {
                    setItemData((prevData) => ({
                      ...prevData,
                      price: value,
                    }));
                  }
                }}
                placeholder="Price"
                className="classic-input"
                type="number"
              />
            </div>
          </div>

          <div className="modal-label-div">
            <label htmlFor="itemDescription" className="modal-label">
              Item Description
            </label>

            <select
              className="language-select"
              onChange={(e) => {
                const selectedLanguage = e.target.value;
                setItemData((prevData) => {
                  return {
                    ...prevData,
                    selectedDescriptionLang: selectedLanguage,
                  };
                });
              }}
            >
              {languageList.map((language) => {
                return <option key={language}>{language}</option>;
              })}
            </select>
          </div>
          <textarea
            id="itemDescription"
            value={itemData.description[itemData.selectedDescriptionLang]}
            onChange={(e) => {
              setItemData((prevData) => ({
                ...prevData,
                description: {
                  ...prevData.description,
                  [prevData.selectedDescriptionLang]: e.target.value,
                },
              }));
            }}
            name="itemDescription"
            placeholder="Item description"
            className="classic-text-area mb-16"
            maxLength={150}
          />

          <div className="image-remove-p-div">
            <p
              onClick={() => {
                setItemData((prevData) => ({
                  ...prevData,
                  image: null,
                }));
              }}
              className="image-remove-p"
            >
              Remove Image
            </p>
          </div>
          <div className="image-input-div">
            <div
              className={
                itemData.image ? "image-input border-none" : "image-input"
              }
            >
              {itemData.image ? (
                <>
                  <img
                    className="image-output"
                    src={
                      itemData.image.url
                        ? itemData.image.url
                        : URL.createObjectURL(itemData.image)
                    }
                  />
                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setItemData((prevState) => ({
                          ...prevState,
                          image: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              ) : (
                <>
                  <img className="image-placeholder" src={image} />

                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setItemData((prevState) => ({
                          ...prevState,
                          image: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              )}
            </div>
          </div>

          <div className="checkbox-container">
            <input
              checked={itemData.available}
              onChange={(e) => {
                setItemData((prevData) => ({
                  ...prevData,
                  available: e.target.checked,
                }));
              }}
              className="checkbox-input"
              type="checkbox"
              id="customCheckbox"
            />
            <label htmlFor="customCheckbox" className="custom-checkbox"></label>
            <p className="checkbox-p">Available</p>
          </div>

          {error && <p className="login-form-error-p">{error}</p>}
        </Modal>
      )}

      {activeModal === "editItem" && (
        <Modal
          heading={"Add Item"}
          paragraph={"Enter information about your item."}
          closeModal={() => {
            setActiveModal("");
            setEditItemData({});
            setError("");
          }}
          btnText={"Save"}
          isLoading={loading === "editItem"}
          btnFuntion={loading === "editItem" ? null : editItem}
        >
          <div className="modal-label-div">
            <label htmlFor="itemName" className="modal-label">
              Item Name *
            </label>

            <select
              className="language-select"
              onChange={(e) => {
                const selectedLanguage = e.target.value;
                setEditItemData((prevData) => {
                  return { ...prevData, selectedNameLang: selectedLanguage };
                });
              }}
            >
              {languageList.map((language) => {
                return <option key={language}>{language}</option>;
              })}
            </select>
          </div>
          <input
            id="itemName"
            value={editItemData.name[editItemData.selectedNameLang]}
            onChange={(e) => {
              setEditItemData((prevData) => ({
                ...prevData,
                name: {
                  ...prevData.name,
                  [prevData.selectedNameLang]: e.target.value,
                },
              }));
            }}
            name="name"
            placeholder="Item name"
            className="classic-input mb-16"
            maxLength={50}
          />

          <div className="modal-double-input-div mb-16">
            <div className="modal-half-input-div">
              <label htmlFor="weight" className="modal-label padding-5-0">
                Weight
              </label>
              <input
                id="weight"
                htmlFor="weight"
                maxLength={10}
                value={editItemData?.weight}
                onChange={(e) => {
                  setEditItemData((prevData) => ({
                    ...prevData,
                    weight: e.target.value,
                  }));
                }}
                name="weight"
                placeholder="Weight"
                className="classic-input"
              />
            </div>

            <div className="modal-half-input-div">
              <label htmlFor="price" className="modal-label padding-5-0">
                Price *
              </label>
              <input
                id="price"
                htmlFor="price"
                name="price"
                maxLength={20}
                value={editItemData.price}
                onChange={(e) => {
                  const value = e.target.value;
                  // Regex to allow only digits and one dot for decimals, disallowing + and - signs
                  if (/^\d*\.?\d{0,2}$/.test(value)) {
                    setEditItemData((prevData) => ({
                      ...prevData,
                      price: value,
                    }));
                  }
                }}
                placeholder="Price"
                className="classic-input"
                type="number"
              />
            </div>
          </div>

          <div className="modal-label-div">
            <label htmlFor="itemDescription" className="modal-label">
              Item Description
            </label>

            <select
              className="language-select"
              onChange={(e) => {
                const selectedLanguage = e.target.value;
                setEditItemData((prevData) => {
                  return {
                    ...prevData,
                    selectedDescriptionLang: selectedLanguage,
                  };
                });
              }}
            >
              {languageList.map((language) => {
                return <option key={language}>{language}</option>;
              })}
            </select>
          </div>
          <textarea
            id="itemDescription"
            value={
              editItemData.description[editItemData.selectedDescriptionLang]
            }
            onChange={(e) => {
              setEditItemData((prevData) => ({
                ...prevData,
                description: {
                  ...prevData.description,
                  [prevData.selectedDescriptionLang]: e.target.value,
                },
              }));
            }}
            name="itemDescription"
            placeholder="Item description"
            className="classic-text-area mb-16"
            maxLength={150}
          />

          <div className="image-remove-p-div">
            <p
              onClick={() => {
                setEditItemData((prevData) => ({
                  ...prevData,
                  newImage: null,
                  image: null,
                }));
              }}
              className="image-remove-p"
            >
              Remove Image
            </p>
          </div>
          <div className="image-input-div">
            <div
              className={
                !editItemData?.image && !editItemData.newImage
                  ? "image-input"
                  : "image-input border-none"
              }
            >
              {editItemData.image || editItemData.newImage ? (
                <>
                  <img
                    className="image-output"
                    src={
                      editItemData.newImage
                        ? URL.createObjectURL(editItemData.newImage)
                        : editItemData.image.url
                    }
                  />
                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setEditItemData((prevState) => ({
                          ...prevState,
                          newImage: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              ) : (
                <>
                  <img className="image-placeholder" src={image} />

                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setEditItemData((prevState) => ({
                          ...prevState,
                          newImage: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              )}
            </div>
          </div>

          <div className="checkbox-container">
            <input
              checked={editItemData.available}
              onChange={(e) => {
                setEditItemData((prevData) => ({
                  ...prevData,
                  available: e.target.checked,
                }));
              }}
              className="checkbox-input"
              type="checkbox"
              id="customCheckbox"
            />
            <label htmlFor="customCheckbox" className="custom-checkbox"></label>
            <p className="checkbox-p">Available</p>
          </div>

          {error && <p className="login-form-error-p">{error}</p>}
        </Modal>
      )}

      {deleteModal.type === "deleteItem" && (
        <DeleteModal
          heading={"Delete item?"}
          paragraph={"Are you sure that you want to delete this item?"}
          deleteFunction={loading === "deleteItem" ? null : deleteItem}
          setDeleteModal={setDeleteModal}
          btnText="Delete"
        ></DeleteModal>
      )}

      <h1 className="page-content-h">
        {category?.name &&
          languageList.length > 0 &&
          category.name[languageList[0]]}
      </h1>
      <p className="page-content-p">
        Edit your category categories and items with ease.
      </p>

      <div className="menu-editor-container">
        <Link
          className="menu-editor-back-btn"
          to="#"
          onClick={() => window.history.back()}
        >
          <img className="menu-editor-back-img" src={back} />
          <p className="menu-editor-back-p">GO BACK</p>
        </Link>

        <div className="menu-editor-items-div">
          {subCategories.map((category) => {
            return (
              <div
                key={category.id}
                className="menu-editor-item-div"
                tabIndex="0"
              >
                <img
                  className="menu-editor-item-img"
                  src={category?.image?.url || noImage}
                />

                <div className="menu-editor-item-p-div">
                  <p className="menu-editor-item-p">
                    {category.name[languageList[0]]}
                  </p>
                </div>

                <div className="menu-editor-item-buttons">
                  <Link
                    to={`${location.pathname}/${category.id}`}
                    className="menu-editor-item-btn"
                  >
                    Enter
                  </Link>
                  <div
                    onClick={() => {
                      setActiveModal("editCategory");
                      setEditCategoryData({
                        ...category,
                        selectedNameLang: languageList[0],
                        selectedDescriptionLang: languageList[0],
                      });
                    }}
                    className="menu-editor-item-btn"
                  >
                    Edit
                  </div>
                  <div
                    onClick={() => {
                      setDeleteModal({
                        type: "deleteCategory",
                        id: category.id,
                      });
                    }}
                    className="menu-editor-item-btn"
                  >
                    Delete
                  </div>
                </div>
              </div>
            );
          })}

          {items.map((item) => {
            return (
              <div key={item.id} className="menu-editor-item-div" tabIndex="0">
                <img
                  className="menu-editor-item-img"
                  src={item?.image?.url || noImage}
                />

                <p className="menu-editor-item-p">
                  {item.name[languageList[0]]}
                </p>

                <p className="menu-editor-item-weight">{item?.weight}</p>
                <p className="menu-editor-item-price">
                  {`${item?.price?.toFixed(2)}${menuCurrency}`}
                </p>

                <div className="menu-editor-item-buttons">
                  <div
                    onClick={() => {
                      setActiveModal("editItem");
                      setEditItemData({
                        ...item,
                        selectedNameLang: languageList[0],
                        selectedDescriptionLang: languageList[0],
                      });
                    }}
                    className="menu-editor-item-btn"
                  >
                    Edit
                  </div>
                  <div
                    onClick={() => {
                      setDeleteModal({
                        type: "deleteItem",
                        id: item.id,
                      });
                    }}
                    className="menu-editor-item-btn"
                  >
                    Delete
                  </div>
                </div>
              </div>
            );
          })}

          <div className="menu-editor-add-item-div" tabIndex="0">
            <img className="menu-editor-add-img" src={add} />

            <div className="menu-editor-item-buttons">
              {items?.length === 0 && (
                <div
                  onClick={() => {
                    setActiveModal("addCategory");
                  }}
                  className="menu-editor-item-btn"
                >
                  Add Category
                </div>
              )}

              {subCategories?.length === 0 && (
                <div
                  onClick={() => {
                    setActiveModal("addItem");
                  }}
                  className="menu-editor-item-btn"
                >
                  Add Item
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Navigation>
  );
};

export default CategoryPage;
